import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AdminMember } from "@coco-mailrelay/shared-types";

/**
 * Admin session store (docs/305 §4.3). Authentication is carried by the backend's
 * httpOnly `admin_token` cookie (HttpOnly; SameSite=Strict; Secure in prod), which
 * `authenticateAdmin` accepts and `apiRequest` sends via `credentials: "include"`.
 *
 * SECURITY: the 7-day session JWT is held in memory ONLY and is deliberately
 * NOT persisted to localStorage (see `partialize` below). Persisting a long-lived
 * admin JWT would let any XSS exfiltrate it, defeating the httpOnly cookie. Only
 * the non-secret `member` / `isAuthenticated` view is persisted so the SPA can
 * re-render the logged-in shell across reloads; the cookie re-establishes auth,
 * and a missing/expired cookie surfaces as a centralized 401 → logout.
 *
 * Cleared on logout and on any centralized 401 (see lib/apiRequest.ts).
 */
interface AuthState {
  token: string | null;
  member: AdminMember | null;
  isAuthenticated: boolean;
  login: (token: string, member: AdminMember) => void;
  setMember: (member: AdminMember) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      member: null,
      isAuthenticated: false,
      login: (token, member) => set({ token, member, isAuthenticated: true }),
      setMember: (member) => set({ member }),
      logout: () => set({ token: null, member: null, isAuthenticated: false }),
    }),
    {
      name: "coco-mailrelay-admin-auth",
      // Never persist the session token — keep it in memory only.
      partialize: (state) => ({ member: state.member, isAuthenticated: state.isAuthenticated }),
    },
  ),
);
